import React, { useState, useEffect, lazy, Suspense, useRef } from 'react';
import { createPortal } from 'react-dom';
import {Link} from 'react-router-dom';
import {Helmet} from 'react-helmet' 
import AnimatedText from './ui/AnimatedText';
import CursorAura from './cursor_aura';

const LinkBox = lazy(() => 
import (`./link_box`))

const LinkBox2 = lazy(() => 
import (`./link_box2`))

const LinkBox3 = lazy(() => 
import (`./link_box3`))

const activeLocations = [
    {
        id: 'bethlehem-pa',
        name: 'Bethlehem, PA',
        zip: '18020',
        descriptor: 'Flagship COSMETINK Studio & Academy',
        address: '3859 Nazareth Pike, Bethlehem, PA 18020',
        studioUrl: 'https://studio.cosmetink.com',
        academyUrl: 'https://academy.cosmetink.com'
    }
];

const chatNodes = {
    root: {
        title: 'How can we help?',
        content: [
            { type: 'p', text: 'Tap a topic for quick answers about COSMETINK Studio services, policies, and prep.' }
        ],
        options: [
            { label: 'Services', next: 'services' },
            { label: 'Policies', next: 'policies' },
            { label: 'Pre-Care', next: 'precare' },
            { label: 'The Process', next: 'process' },
            { label: 'Financing', next: 'financing' },
            { label: 'About Us', next: 'about' },
            { label: 'Contact / Book', next: 'contact' }
        ]
    },
    services: {
        title: 'Services',
        content: [
            { type: 'p', text: 'Choose a service to learn what it is, who it’s for, and typical pricing.' }
        ],
        options: [
            { label: 'Microblading / Nanoblading', next: 'micro-nano' },
            { label: 'Ombré Brows', next: 'ombre' },
            { label: 'Combo Brows', next: 'combo' },
            { label: 'Lip Blush', next: 'lip' },
            { label: 'Lash Enhancement / Eyeliner', next: 'liner' },
            { label: 'Pricing Range', next: 'pricing' }
        ]
    },
    'micro-nano': {
        title: 'Microblading vs. Nanoblading',
        content: [
            { type: 'p', text: 'Both create natural, hair-like strokes for fuller brows.' },
            { type: 'list', items: [
                'Microblading: handheld tool; best for dryer skin with smaller pores.',
                'Nanoblading: PMU machine; works for most skin types, especially oilier skin; less traumatic and more customizable.'
            ]},
            { type: 'p', text: 'If you’re unsure, our artists help choose the best method at your appointment.' }
        ]
    },
    ombre: {
        title: 'Ombré Brows',
        content: [
            { type: 'p', text: 'A misty, powder-filled brow (like makeup or tint), lighter at the front and darker at the tail.' },
            { type: 'list', items: [
                'Great for all skin types, especially oilier skin.',
                'Customizable for soft natural or bold definition.'
            ]}
        ]
    },
    combo: {
        title: 'Combo Brows',
        content: [
            { type: 'p', text: 'A mix of hairstrokes + shading for a natural yet filled-in look.' },
            { type: 'list', items: [
                'Best for normal to dryer skin (nano combo can work for oilier skin).',
                'Adds more fullness than hairstrokes alone.'
            ]}
        ]
    },
    lip: {
        title: 'Lip Blush',
        content: [
            { type: 'p', text: 'A tinted lip-balm effect that enhances color, shape, and symmetry.' },
            { type: 'list', items: [
                'Healing: swelling for 1–2 days is normal.',
                'Darker lips may need 3–4 sessions to neutralize.'
            ]},
            { type: 'p', text: 'Filler timing: 4 weeks before or after your session.' }
        ]
    },
    liner: {
        title: 'Lash Enhancement / Eyeliner',
        content: [
            { type: 'p', text: 'Defines the lash line to make lashes look fuller.' },
            { type: 'list', items: [
                'Optional wing may be possible depending on eye shape.',
                'COSMETINK prefers timeless, natural wings.'
            ]}
        ]
    },
    pricing: {
        title: 'Pricing Range',
        content: [
            { type: 'list', items: [
                'Brows (Micro/Nano/Ombré/Combo): $500–$700 + mandatory perfecting session $150.',
                'Lip Blush: $500–$700 + mandatory perfecting session $150.',
                'Lash Enhancement / Eyeliner: $400–$500 + mandatory perfecting session $150.'
            ]}
        ]
    },
    policies: {
        title: 'Policies (Key Points)',
        content: [
            { type: 'list', items: [
                'Deposits are required and non-refundable.',
                'Late cancellation/no-show (<48 hours) incurs 50% of remaining balance (or 100% for touch-ups).',
                'Arriving 10+ minutes late requires reschedule and may forfeit deposit.',
                'Prior PMU work must be approved by photo before booking.'
            ]},
            { type: 'p', text: 'By booking, you accept all policies.' }
        ]
    },
    precare: {
        title: 'Pre-Care (Highlights)',
        content: [
            { type: 'list', items: [
                'No Accutane for 1 year; no spironolactone 6 months before and 4 weeks after 2nd session.',
                'No pregnancy or breastfeeding.',
                'No Botox/fillers 4 weeks before/after.',
                'Stop retinol/tretinoin weeks before; avoid exfoliators 4 weeks prior.',
                'Avoid sun/tanning 2 weeks prior; no alcohol or blood thinners 24–48 hours prior.'
            ]},
            { type: 'p', text: 'Eyeliner and lip blush have extra prep requirements.' }
        ]
    },
    process: {
        title: 'The Process',
        content: [
            { type: 'list', items: [
                'Consultation (15–20 min): discuss goals, routine, consent.',
                'Mapping (20–30 min): custom outline, approve shape.',
                'Procedure (1.5–2.5 hours): numbing throughout.'
            ]},
            { type: 'p', text: 'Healing: brows 10–14 days; lips/eyeliner ~7 days.' }
        ]
    },
    financing: {
        title: 'Financing',
        content: [
            { type: 'list', items: [
                '30-second approval',
                '0% APR options available',
                'Payments as low as $30/month',
                'No hard credit check'
            ]}
        ],
        actions: [
            { label: 'Apply Now', href: 'https://studio.cosmetink.com' }
        ]
    },
    about: {
        title: 'About COSMETINK',
        content: [
            { type: 'p', text: 'Nikky & Sean are a sibling duo with 20+ years combined experience.' },
            { type: 'p', text: 'Nikky is a licensed esthetician; Sean has a Chemistry degree. Together they lead a team focused on precise, natural results.' }
        ]
    },
    contact: {
        title: 'Contact / Book',
        content: [
            { type: 'p', text: 'Ready to book or see artist availability and pricing?' }
        ],
        actions: [
            { label: 'Book Services', href: 'https://studio.cosmetink.com' },
            { label: 'Email Us', href: 'mailto:cosmetinkbeauty@gmail.com' }
        ]
    }
};

const renderChatContent = (content) => (
    content.map((block, idx) => {
        if (block.type === 'list') {
            return (
                <ul key={`list-${idx}`} className="orb-chat-list">
                    {block.items.map((item) => (
                        <li key={item}>{item}</li>
                    ))}
                </ul>
            );
        }
        return (
            <p key={`p-${idx}`} className="orb-chat-text">{block.text}</p>
        );
    })
);





export default function LandingPage(props)  {
    const [menu, setMenu] = useState(false)

    const [timer, setTimer] = useState(false)
    const [showOrb, setShowOrb] = useState(false)
    const [chatPath, setChatPath] = useState(['root'])
    const [locatorQuery, setLocatorQuery] = useState('')
    const [hasSearched, setHasSearched] = useState(false)
    const [locatorReveal, setLocatorReveal] = useState(false)
    const year = new Date().getFullYear()
    const featuredLocation = activeLocations[0]
    const locatorResultRef = useRef(null)


    useEffect(() => {
       setTimeout(() => {
         setTimer(true)
       }, 1500);
    })

    useEffect(() => {
  const items = document.querySelectorAll(".cos-reveal");

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.18 }
  );

  items.forEach((item) => observer.observe(item));

  return () => observer.disconnect();
}, []);

    const handleLocatorSubmit = (e) => {
      e.preventDefault();
      setHasSearched(true);
      setLocatorReveal(false);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setLocatorReveal(true);
        });
      });
    };

    const locationResult = featuredLocation;

    useEffect(() => {
      if (!hasSearched || !locatorReveal || !locatorResultRef.current) return;

      locatorResultRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }, [hasSearched, locatorReveal]);



        return (
            <>
            <Helmet>
                <meta charSet="utf-8" />
                <title>Permanent Makeup Studio & Training Academy | Bethlehem, Easton, Allentown, Philly, New York | Pennsylvania, New Jersey, New York, Delaware, Maryland, and beyond</title> 
                <link rel="canonical" href="https://www.cosmetink.com/" />
                <meta name="description" content="Beginner Certification Classes - Learn Microblading, Ombre Brows, Lip Blush - NO EXPERIENCE NEEDED - NO ESTHETICIAN LICENSE REQUIRED" />
                <meta name="keywords" content="ombre brows training, permanent makeup training, permanent makeup school, permanent makeup classes beginners, microblading certification, ombre brows certification, lip blush training, lip blush certification, best microblading training, aamp training, beauty school pa, learn microblading, tattoo makeup, cosmetic ink, permanent makeup, microblading, best microblading, semi-permanent makeup, microbladed eyebrows, microblading de cejas" />

            </Helmet>    
            <CursorAura />

        
  
            <div className='main-body-container'>
                <div className='main-title c blk'>

               <h1 className="title2 wht c studio">
  <span className="logo-lockup-top">
    <span className="logo-cosmet">COSMET</span>
    <span className="moving-grad2">INK</span>
    <span className="reg-nav">®</span>
  </span>
  <span className="logo-lockup-bottom">STUDIO &amp; ACADEMY</span>
</h1>
                 
                   <div className="title3">
Advanced Permanent Makeup.
  </div>

                 <div className="luxury-nav">
  <a href="https://studio.cosmetink.com">Studio</a>
  <a href="https://academy.cosmetink.com">Academy</a>
  <a href="https://shop.cosmetink.com">PRO Shop</a>
  <a href="/franchise">Franchise</a>
</div>

                    </div>

                <section className="locator-shell" aria-labelledby="locator-title">
                    <div className="locator-panel">
                            <div className="locator-copy">
                            <div className="locator-kicker">Location Search</div>
                            <h2 id="locator-title" className="locator-title">Find Your Nearest COSMETINK Location</h2>
                            <p className="locator-text">
                                Search by city or zip code to find the nearest COSMETINK location.
                            </p>
                        </div>

                        <form className="locator-search" onSubmit={handleLocatorSubmit}>
                            <label className="sr-only" htmlFor="cosmetink-location-search">Search city or zip code</label>
                            <div className="locator-input-wrap">
                                <span className="locator-search-icon" aria-hidden="true">
                                    <svg viewBox="0 0 24 24" fill="none" role="presentation">
                                        <circle cx="11" cy="11" r="6.5" />
                                        <path d="M16 16L21 21" />
                                    </svg>
                                </span>
                                <input
                                    id="cosmetink-location-search"
                                    className="locator-input"
                                    type="text"
                                    inputMode="search"
                                    placeholder="Enter city or zip code"
                                    value={locatorQuery}
                                    onChange={(e) => setLocatorQuery(e.target.value)}
                                />
                                <button className="locator-submit" type="submit">Search</button>
                            </div>
                        </form>

                        {hasSearched ? (
                            <div
                                className={`locator-result ${locatorReveal ? 'locator-result--visible' : 'locator-result--pre'}`}
                                ref={locatorResultRef}
                            >
                                <div className="locator-result-top">
                                    <div>
                                        <div className="locator-result-label">
                                            {locatorQuery.trim()
                                                ? `Nearest result for "${locatorQuery.trim()}"`
                                                : 'Nearest location'}
                                        </div>
                                        <div className="locator-result-title">{locationResult.name} {locationResult.zip}</div>
                                        <div className="locator-result-subtitle">{locationResult.descriptor}</div>
                                        <div className="locator-result-address">{locationResult.address}</div>
                                    </div>
                                </div>

                                <div className="locator-actions">
                                    <a className="locator-link locator-link--studio" href={locationResult.studioUrl} target="_blank" rel="noreferrer">Visit Studio</a>
                                    <a className="locator-link locator-link--academy" href={locationResult.academyUrl} target="_blank" rel="noreferrer">View Academy</a>
                                </div>

                                <div className="locator-expansion locator-expansion--result">
                                    <div className="locator-expansion-head">
                                        <span className="locator-expansion-icons" aria-hidden="true">
                                            <span className="locator-expansion-icon">✦</span>
                                        </span>
                                        <span className="locator-expansion-title">We’re Actively Expanding</span>
                                    </div>
                                    <p className="locator-expansion-copy">
                                        If you’re interested in opening a COSMETINK franchise location or bringing COSMETINK
                                        into your current space, <a href="/franchise">learn more here.</a>
                                    </p>
                                </div>
                            </div>
                        ) : null}
                    </div>
                </section>

              
  

                
         
{/*
                <div className="awards-editorial" aria-label="Awards and recognitions">
                    <div className="awards-editorial-kicker">Award-Winning</div>
                    <div className="awards-editorial-grid">
                        <div className="awards-editorial-hero">
                            <div className="awards-badge-frame awards-badge-frame-hero">
                                <img className="awards-badge awards-badge-zoom" alt="Best of 2026" src="https://cosmetinklanding.s3.us-east-2.amazonaws.com/bestof2026.jpg" />
                            </div>
                        </div>
                        <div className="awards-editorial-support">
                            <div className="awards-badge-frame awards-badge-frame-support">
                                <img className="awards-badge" alt="Best of the Lehigh Valley 2025" src="https://lvltravel.s3.us-east-2.amazonaws.com/IMG_7951.JPG" />
                            </div>
                            <div className="awards-badge-frame awards-badge-frame-support">
                                <img className="awards-badge" alt="Best of the Lehigh Valley 2024" src="https://cosmetinklanding.s3.us-east-2.amazonaws.com/lvbestof2024.PNG" />
                            </div>
                        </div>
                    </div>
                </div>

                */}

               <div className='main-body-container2'>   
               
             <Suspense fallback={<div>
                      </div>}>
      
               <LinkBox />


               <LinkBox2 />

               <LinkBox3 />
               </Suspense>
               </div>    

             

              <div className="site-footer">
  <div className="site-footer-inner">
    <div className="footer-brand">
      <div className="footer-logo">
        COSMET<span className="footer-ink">INK</span> <span className="footer-reg">®</span>
      </div>
      <div className="footer-sub">Permanent Cosmetic Professionals</div>
    </div>

    <div className="footer-meta">
      <div className="footer-line">3859 Nazareth Pike • Bethlehem, PA 18020</div>
      <div className="footer-line subtle">© {year} COSMETINK. All rights reserved.</div>
    </div>

    <div className="footer-links">
      <a href="/franchise">Franchise</a>
      <a href="https://academy.cosmetink.com" target="_blank" rel="noreferrer">Academy</a>
      <a href="https://studio.cosmetink.com" target="_blank" rel="noreferrer">Studio</a>
      <a href="https://shop.cosmetink.com" target="_blank" rel="noreferrer">Shop</a>
    </div>
  </div>
</div>

            </div>

            {typeof document !== 'undefined' && showOrb
                ? createPortal(
                    <div className="orb-widget" aria-live="polite">
                        <div className="orb-core">
                            <img
                                src="https://cosacademyassets.s3.us-east-2.amazonaws.com/profile/IMG_9818.PNG"
                                alt="Cosmetink welcome"
                            />
                        </div>
                        <div className="orb-bubble orb-chat">
                            <div className="orb-chat-header">
                                <span className="orb-bubble-text">
                                    <AnimatedText text="Hi! We’re Nikky & Sean — welcome to COSMETINK!" speed={20} chunkSize={2} />
                                </span>
                                <button
                                    className="orb-close"
                                    aria-label="Close welcome message"
                                    onClick={() => setShowOrb(false)}
                                    type="button"
                                >
                                    ×
                                </button>
                            </div>
                            <div className="orb-chat-body">
                                <div className="orb-chat-title">{chatNodes[chatPath[chatPath.length - 1]].title}</div>
                                {renderChatContent(chatNodes[chatPath[chatPath.length - 1]].content)}
                            </div>
                            <div className="orb-chat-actions">
                                {chatNodes[chatPath[chatPath.length - 1]].options?.map((option) => (
                                    <button
                                        key={option.label}
                                        className="orb-chat-btn"
                                        type="button"
                                        onClick={() => setChatPath((prev) => [...prev, option.next])}
                                    >
                                        {option.label}
                                    </button>
                                ))}
                                {chatNodes[chatPath[chatPath.length - 1]].actions?.map((action) => (
                                    <a
                                        key={action.label}
                                        className="orb-chat-btn orb-chat-link"
                                        href={action.href}
                                        target={action.href.startsWith('http') ? "_blank" : undefined}
                                        rel={action.href.startsWith('http') ? "noreferrer" : undefined}
                                    >
                                        {action.label}
                                    </a>
                                ))}
                            </div>
                            <div className="orb-chat-footer">
                                <button
                                    className="orb-chat-secondary"
                                    type="button"
                                    onClick={() => setChatPath((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev))}
                                    disabled={chatPath.length === 1}
                                >
                                    Back
                                </button>
                                <button
                                    className="orb-chat-secondary"
                                    type="button"
                                    onClick={() => setChatPath(['root'])}
                                >
                                    Start Over
                                </button>
                            </div>
                        </div>
                    </div>,
                    document.body
                  )
                : null}
            </>
            ) 
       
        
    }
